import { FC } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  Icon,
  ListItem,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { BiInfoCircle } from 'react-icons/bi';

import { ValidatedFilenameErrorCode } from '../../graphql/generated';
import { pluralize } from '../../utils/pluralize/pluralize';

export type ValidationErrorMessage = {
  id: string;
  message: JSX.Element;
};

export type ValidationError = Record<
  ValidatedFilenameErrorCode,
  ValidationErrorMessage[]
>;

type ErrorCodeLabelConfig = {
  action: string;
  tooltip: string;
};

type TValidatedFilenameErrorCodeLabelMap = Record<
  ValidatedFilenameErrorCode,
  ErrorCodeLabelConfig
>;

type ConfirmationProps = Props & {
  onConfirm?: () => void;
};

type Props = {
  onCancel: () => void;
  errors: ValidationError;
};

const ValidatedFilenameErrorCodeLabelMap: TValidatedFilenameErrorCodeLabelMap = {
  [ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT]: {
    action: 'will not be uploaded',
    tooltip: 'Filenames must not contain excessive characters',
  },
  [ValidatedFilenameErrorCode.INVALID_CHARACTERS]: {
    action: 'will be renamed',
    tooltip: 'Filenames must not contain special characters',
  },
  [ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE]: {
    action: 'will not be uploaded',
    tooltip: 'This file type is not supported',
  },
};

export const FileValidationConfirmationModalContent: FC<ConfirmationProps> = ({
  onCancel,
  onConfirm,
  errors,
}) => {
  return (
    <ModalContent data-testid="file-validation-confirmation">
      <ModalHeader fontSize="lg" fontWeight="bold">
        Confirm changes?
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gridGap="6">
          <Text fontWeight="500">
            File validation errors were detected, please review the amendment
            list before continuing.
          </Text>
          <ValidationErrorAccordion errors={errors} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} ml={3} colorScheme="green">
          Confirm Upload
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export const FileValidationFailingModalContent: FC<Props> = ({
  onCancel,
  errors,
}) => {
  return (
    <ModalContent data-testid="file-validation-confirmation">
      <ModalHeader fontSize="lg" fontWeight="bold">
        Upload cancelled
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gridGap="6">
          <Text fontWeight="500">
            File validation errors were detected, please review the amendment
            list and try again.
          </Text>
          <ValidationErrorAccordion errors={errors} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Discard</Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ValidationErrorAccordion = ({ errors }: Pick<Props, 'errors'>) => {
  const errorList = Object.entries(errors);
  return (
    <Accordion allowMultiple>
      {errorList.map(([code, messages]) => {
        if (isEmpty(messages)) return null;

        const errorCode = code as ValidatedFilenameErrorCode;
        const errorLabel = ValidatedFilenameErrorCodeLabelMap[errorCode];

        return (
          <AccordionItem key={code} data-testid="validation-error-type">
            <h2>
              <AccordionButton data-testid="validation-error-show-details">
                <Flex flex="1" justifyContent="space-between">
                  <Flex gridGap="2">
                    <Text as="span" fontWeight="semibold">
                      {messages.length} {pluralize('file', messages.length)}{' '}
                      {errorLabel.action}
                    </Text>
                    <Tooltip placement="right" label={errorLabel.tooltip}>
                      <span>
                        <Icon as={BiInfoCircle} />
                      </span>
                    </Tooltip>
                  </Flex>
                  <AccordionIcon />
                </Flex>
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <UnorderedList spacing={3}>
                {messages.map(({ id, message }) => (
                  <ListItem key={id}>{message}</ListItem>
                ))}
              </UnorderedList>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
